import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef } from 'react';
import { USER_CONVERSATION_ID } from '../../../api/conversations.api';
import { queryKeys, vdKeys } from '../../../api/queryKeys';
import { isAdmin, isDeliveries } from '../../../helpers/appType';
import { useDeliveriesOrg } from '../../../store/deliveriesOrg';
import { ConversationListItemResponse } from '../../../types/conversation.types';
import { readConversation } from '../conversationTransport';
import { clearUserGroupUnread } from '../groupsCache';

/**
 * Marks a thread read and keeps the unread badge honest.
 *
 * The badge is never computed on the client. `POST .../read` answers with the
 * caller's fresh counts, so the response is written straight into the cache -
 * a local increment/decrement would drift the moment a second tab is open, a
 * message is deleted, or read-all fires somewhere else.
 *
 * Both apps get an authoritative shape back, just a different one: admin gets
 * `{ threads, messages, ... }`, the merchant `{ unread, support, groups, ... }`
 * - from the support thread's route and a group's alike. Each is exactly what
 * its own badge query returns, so both can be written rather than invalidated.
 */
export const useChatRead = (conversationId: number | string | null) => {
  const queryClient = useQueryClient();
  const inFlightRef = useRef(false);

  const markRead = useCallback(async () => {
    if (conversationId === null || inFlightRef.current) return;
    try {
      inFlightRef.current = true;
      const { data } = await readConversation(conversationId);

      queryClient.setQueryData(queryKeys.conversations.unreadCount, data.data);

      /* A merchant group's row in the Groups tab, which the merchant keeps by
         hand rather than refetching - see `groupsCache`. The support thread
         has no row anywhere. */
      if (
        !isAdmin &&
        !isDeliveries &&
        conversationId !== USER_CONVERSATION_ID
      ) {
        clearUserGroupUnread(queryClient, conversationId);
      }

      // The row's unread pill is now stale.
      if (isAdmin) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.conversations.inboxAll,
        });
        /* The Viktor card's row is not in that list - it is patched in place,
           the same way the socket bumps it. A no-op for every other thread
           and until the chat page has fetched it once. */
        queryClient.setQueryData<ConversationListItemResponse>(
          queryKeys.conversations.viktor,
          (row: ConversationListItemResponse | undefined) =>
            row && row.id === Number(conversationId) && row.unread > 0
              ? { ...row, unread: 0 }
              : row
        );
      }

      // The vDrive console answers `{ chats, messages }` per organization:
      // its badge and inbox live under their own keys.
      if (isDeliveries) {
        const orgId = useDeliveriesOrg.getState().selectedOrgId;
        if (orgId !== null) {
          queryClient.setQueryData(vdKeys.chatUnread(orgId), data.data);
          queryClient.invalidateQueries({
            queryKey: vdKeys.chatInboxAll(orgId),
          });
        }
      }
    } catch {
      // Read receipts are best-effort - a failure here must never interrupt
      // reading or sending, and the next new message retries it anyway.
    } finally {
      inFlightRef.current = false;
    }
  }, [conversationId, queryClient]);

  return { markRead };
};
